import {FIELD_FLIGHT_SOUNDS} from '../../configs/fieldAmbienceSounds';
import {getFieldInsets} from '../visuals/fieldInsets';

/**
 * Видит ли игрок птиц: голос стаи или орла слышен, только пока они в кадре
 * (см. scenes/FieldAmbienceSounds). Пейзаж вокруг поля шире экрана, и стая
 * большую часть пути летит за его краем — крик оттуда звучал бы «из ниоткуда».
 *
 * Кадр — то, что показывает камера сцены, кроме закрытого панелями интерфейса.
 * Где камеры нет (заглушки сцен в тестах), птицы считаются видимыми: голоса
 * работают, как без этой проверки.
 */

// Сколько раз заглянуть в будущее полета, оценивая его время в кадре
const SAMPLES = 80;

// НЕПРОЗРАЧНЫЕ ПАНЕЛИ ИНТЕРФЕЙСА, которые лежат ПОВЕРХ холста игры: птиц под
// ними игрок не видит.
//  - нижняя панель (components/Footer) закрывает низ холста;
//  - экран представления игроков перед началом партии (components/GameContent)
//    закрывает его целиком — сцена под ним уже идет.
// Выпадающие списки шапки (игроки, рынок политик) — высотой по содержимому,
// низа холста не закрывают и в счет не идут.
// В счет идет панель во всю ширину холста, доходящая до его низа: закрыто все
// от ее верхнего края и ниже. Панель у́же холста не в счет. Холст поля матча
// лежит под всем окном, а футер на десктопе у́же него — его закрытый низ
// берется из отступа свободной области (utils/visuals/fieldInsets)
export const SCREEN_COVER_SELECTORS = Object.freeze(['.footer-container', '.loading-screen-overlay']);
// Края панели и холста сходятся с точностью до дробного пикселя
const EDGE_TOLERANCE_PX = 1;
// Верстку незачем мерить на каждом кадре: панели меняются редко
const COVER_MEASURE_TTL_MS = 300;
// Замеры по холстам: {measuredAt, share}
const coverByCanvas = new WeakMap();

const measureCoveredBottomShare = (canvas) => {
  const canvasRect = canvas.getBoundingClientRect();
  if (!(canvasRect.height > 0) || !(canvasRect.width > 0)) {
    return 0;
  }
  // Верх самой высокой панели, лежащей на холсте: все, что ниже него, закрыто
  let coverTop = canvasRect.bottom;
  document.querySelectorAll(SCREEN_COVER_SELECTORS.join(', ')).forEach((panel) => {
    const rect = panel.getBoundingClientRect();
    const isAcrossCanvas = rect.left <= canvasRect.left + EDGE_TOLERANCE_PX
      && rect.right >= canvasRect.right - EDGE_TOLERANCE_PX;
    const isDownToBottom = rect.bottom >= canvasRect.bottom - EDGE_TOLERANCE_PX;
    if (isAcrossCanvas && isDownToBottom) {
      coverTop = Math.min(coverTop, rect.top);
    }
  });
  const coveredByPanels = (canvasRect.bottom - coverTop) / canvasRect.height;
  const coveredByFieldInset = (getFieldInsets().bottom || 0) / canvasRect.height;
  return Math.min(1, Math.max(0, coveredByPanels, coveredByFieldInset));
};

/**
 * Какая доля высоты кадра снизу закрыта панелями интерфейса
 * @param {Phaser.Scene} scene
 * @returns {number} 0..1; 0 — панелей нет (или мерить негде), 1 — холст закрыт целиком
 */
const resolveCoveredBottomShare = (scene) => {
  try {
    const canvas = scene?.game?.canvas;
    if (!canvas || typeof canvas.getBoundingClientRect !== 'function' || typeof document === 'undefined') {
      return 0;
    }
    const now = Date.now();
    const known = coverByCanvas.get(canvas);
    if (known && now >= known.measuredAt && now - known.measuredAt < COVER_MEASURE_TTL_MS) {
      return known.share;
    }
    const share = measureCoveredBottomShare(canvas);
    coverByCanvas.set(canvas, {measuredAt: now, share});
    return share;
  } catch (error) {
    return 0;
  }
};

/**
 * Кадр игрока в координатах поля — без низа под панелями интерфейса и с
 * отступом от краев: птица, едва показавшаяся у кромки, еще не «на виду»
 * @param {Phaser.Scene} scene
 * @param {number} [hexSize] — размер ячейки: в нем задан отступ
 * @returns {{left: number, right: number, top: number, bottom: number}|null} null — камеры нет
 */
export const resolveViewRect = (scene, hexSize = 0) => {
  const view = scene?.cameras?.main?.worldView;
  if (!view || !(view.width > 0) || !(view.height > 0)) {
    return null;
  }
  const height = view.height * (1 - resolveCoveredBottomShare(scene));
  // На очень маленьком экране (или при сильном приближении) отступ съел бы весь кадр
  const inset = Math.max(0, Math.min(
    (hexSize || 0) * FIELD_FLIGHT_SOUNDS.viewInsetFactor,
    view.width / 4,
    height / 4,
  ));
  return {
    left: view.x + inset,
    right: view.x + view.width - inset,
    top: view.y + inset,
    bottom: view.y + height - inset,
  };
};

// Кадр без высоты (панель закрыла холст целиком) не вмещает никого
const checkIfPointInRect = (rect, x, y) => (
  rect.bottom > rect.top && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
);

/**
 * Есть ли в кадре хоть одна из птиц — по их спрайтам, как они нарисованы сейчас.
 * Невидимый или почти прозрачный спрайт (птица у края пейзажа, орел еще
 * проступает) — не в счет
 * @param {Phaser.Scene} scene
 * @param {Array<Object>} [sprites]
 * @param {number} [hexSize]
 * @returns {boolean}
 */
export const checkIfAnySpriteInView = (scene, sprites, hexSize) => {
  const rect = resolveViewRect(scene, hexSize);
  if (!rect) {
    return true;
  }
  return (sprites || []).some((sprite) => (
    Boolean(sprite)
    && sprite.visible !== false
    && sprite.alpha >= FIELD_FLIGHT_SOUNDS.minVisibleAlpha
    && checkIfPointInRect(rect, sprite.x, sprite.y)
  ));
};

/**
 * Сколько времени птицы проведут в кадре, если игрок не тронет камеру: полет
 * просматривается наперед по своей модели. По этому времени выбирается момент
 * крика — не в самом его начале и не в самом конце
 * @param {Object} params
 * @param {Phaser.Scene} params.scene
 * @param {number} [params.hexSize]
 * @param {number} params.durationMs — сколько длится весь полет
 * @param {function(number): Array<{x: number, y: number, alpha: (number|undefined)}>} params.resolvePoints —
 *   где птицы в заданный момент полета и насколько они непрозрачны (alpha —
 *   та, с которой птица будет нарисована; не задана — птица видна целиком)
 * @returns {number} мс; без камеры — весь полет
 */
export const estimateInViewMs = ({scene, hexSize, durationMs, resolvePoints}) => {
  if (!(durationMs > 0)) {
    return 0;
  }
  const rect = resolveViewRect(scene, hexSize);
  if (!rect) {
    return durationMs;
  }

  let inViewSamples = 0;
  for (let index = 0; index < SAMPLES; index += 1) {
    const points = resolvePoints(((index + 0.5) / SAMPLES) * durationMs) || [];
    const isInView = points.some((point) => (
      (point.alpha ?? 1) >= FIELD_FLIGHT_SOUNDS.minVisibleAlpha && checkIfPointInRect(rect, point.x, point.y)
    ));
    if (isInView) {
      inViewSamples += 1;
    }
  }
  return (inViewSamples / SAMPLES) * durationMs;
};
